<template>
  <div class="gl-detail-page">

    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <router-link to="/owner/customers">Customers</router-link>
            <span class="gl-bc-sep">/</span>
            <span>{{ customer?.name || 'Details' }}</span>
          </div>
          <h1 class="gl-branch-title">Customer Details</h1>
        </div>
        <router-link to="/owner/customers" class="gl-back-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          <span>Back to Customers</span>
        </router-link>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Skeleton -->
      <template v-if="loading">
        <div class="gl-detail-grid">
          <div class="gl-profile-col">
            <div class="gl-profile-card gl-skel-card">
              <div class="gl-profile-hero">
                <div class="gl-skel-avatar-lg"></div>
              </div>
              <div class="gl-profile-body">
                <div class="gl-skel-line w60 mx-auto mb-2"></div>
                <div class="gl-skel-line w40 mx-auto mb-3"></div>
                <div class="gl-skel-badge-sm mx-auto mb-4"></div>
                <div class="gl-skel-spent"></div>
                <div class="gl-profile-meta-list">
                  <div v-for="i in 4" :key="i" class="gl-meta-row">
                    <div class="gl-skel-line w35"></div>
                    <div class="gl-skel-line w45"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="gl-main-col">
            <div class="gl-stats-row">
              <div v-for="i in 3" :key="i" class="gl-stat-box gl-skel-card" style="height:88px"></div>
            </div>
            <div class="gl-tabs-card gl-skel-card" style="height:420px"></div>
          </div>
        </div>
      </template>

      <!-- Loaded -->
      <template v-else-if="customer">
        <div class="gl-detail-grid">

          <!-- LEFT: Profile Card -->
          <div class="gl-profile-col">
            <div class="gl-profile-card">
              <div class="gl-profile-hero">
                <div class="gl-avatar-lg" v-if="customer.avatar && !imgError">
                  <img :src="customer.avatar" alt="" @error="imgError = true">
                </div>
                <div class="gl-avatar-lg gl-avatar-placeholder-lg" v-else>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </div>
              </div>
              <div class="gl-profile-body">
                <h2 class="gl-profile-name">{{ customer.name }}</h2>
                <p class="gl-profile-phone">{{ customer.phone }}</p>
                <p class="gl-profile-email">{{ customer.email || 'No email' }}</p>
                <span class="gl-tier-badge" :class="getTierClass(customer.membership_tier?.name)">
                  {{ customer.membership_tier?.name || 'No Tier' }}
                </span>
                <div class="gl-spent-box">
                  <span class="gl-spent-label">Total spent with you</span>
                  <span class="gl-spent-val">RM {{ formatPrice(customer.stats?.total_spent) }}</span>
                </div>
                <div class="gl-profile-meta-list">
                  <div class="gl-meta-row">
                    <span class="gl-meta-lbl">Member Since</span>
                    <span class="gl-meta-val">{{ formatFullDate(customer.created_at) }}</span>
                  </div>
                  <div class="gl-meta-row">
                    <span class="gl-meta-lbl">Last Login</span>
                    <span class="gl-meta-val">{{ customer.last_login_at ? formatFullDate(customer.last_login_at) : 'Never' }}</span>
                  </div>
                  <div class="gl-meta-row">
                    <span class="gl-meta-lbl">Phone Verified</span>
                    <span class="gl-meta-val" :style="customer.phone_verified_at ? 'color:#900C27;font-weight:600' : 'color:#dc2626;font-weight:600'">
                      {{ customer.phone_verified_at ? 'Yes' : 'No' }}
                    </span>
                  </div>
                  <div class="gl-meta-row" v-if="customer.date_of_birth">
                    <span class="gl-meta-lbl">Birthday</span>
                    <span class="gl-meta-val">{{ formatBirthday(customer.date_of_birth) }}</span>
                  </div>
                  <div class="gl-meta-row" v-if="customer.favorite_outlet">
                    <span class="gl-meta-lbl">Favorite Branch</span>
                    <span class="gl-meta-val">{{ customer.favorite_outlet.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- RIGHT: Stats + Tabs -->
          <div class="gl-main-col">

            <!-- Stats row -->
            <div class="gl-stats-row">
              <div class="gl-stat-box">
                <span class="gl-stat-num">{{ customer.stats?.total_orders ?? 0 }}</span>
                <span class="gl-stat-lbl">Total Orders</span>
              </div>
              <div class="gl-stat-box gl-stat-box-teal">
                <span class="gl-stat-num">RM {{ formatPrice(customer.stats?.total_spent) }}</span>
                <span class="gl-stat-lbl">Total Spent</span>
              </div>
              <div class="gl-stat-box gl-stat-box-blue">
                <span class="gl-stat-num">RM {{ formatPrice(customer.stats?.average_order) }}</span>
                <span class="gl-stat-lbl">Avg. Order</span>
              </div>
            </div>

            <!-- Tabs card -->
            <div class="gl-tabs-card">
              <div class="gl-tab-bar">
                <button class="gl-tab-btn" :class="{ 'gl-tab-active': activeTab === 'orders' }" @click="activeTab = 'orders'">
                  Orders
                  <span class="gl-tab-count">{{ ordersPagination.total }}</span>
                </button>
                <button class="gl-tab-btn" :class="{ 'gl-tab-active': activeTab === 'activity' }" @click="switchToActivity">
                  Activity
                </button>
              </div>

              <!-- Orders tab -->
              <div v-if="activeTab === 'orders'">
                <div v-if="loadingOrders" class="gl-tab-loading">
                  <div class="gl-skel-table-inner">
                    <div v-for="i in 5" :key="i" class="gl-skel-row-inner" :style="`animation-delay:${i*40}ms`">
                      <div class="gl-skel-line w30"></div>
                      <div class="gl-skel-line w20"></div>
                      <div class="gl-skel-line w15"></div>
                      <div class="gl-skel-badge-sm"></div>
                      <div class="gl-skel-line w20"></div>
                      <div class="gl-skel-line w25"></div>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <div class="gl-table-wrap">
                    <table class="gl-table">
                      <thead>
                        <tr>
                          <th>Order #</th>
                          <th>Branch</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th class="th-right">Total</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="order in orders" :key="order.id">
                          <td><span class="gl-order-num">{{ order.order_number }}</span></td>
                          <td class="gl-branch-name">{{ order.outlet?.name || '—' }}</td>
                          <td><span class="gl-type-badge" :class="getTypeClass(order.type)">{{ formatType(order.type) }}</span></td>
                          <td><span class="gl-status-badge" :class="getStatusClass(order.status)">{{ order.status }}</span></td>
                          <td class="td-right">
                            <span class="gl-order-total">RM {{ formatPrice(order.total) }}</span>
                            <span v-if="order.points_earned" class="gl-points-earned">+{{ order.points_earned }} pts</span>
                          </td>
                          <td class="gl-date-cell">{{ formatFullDate(order.created_at) }}</td>
                        </tr>
                        <tr v-if="!orders.length">
                          <td colspan="6" class="gl-empty-cell">No orders found.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="ordersPagination.last_page > 1" class="gl-pagination">
                    <button class="gl-page-btn" :disabled="ordersPagination.current_page === 1" @click="fetchOrders(ordersPagination.current_page - 1)">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                    </button>
                    <span class="gl-page-info">{{ ordersPagination.current_page }} / {{ ordersPagination.last_page }}</span>
                    <button class="gl-page-btn" :disabled="ordersPagination.current_page === ordersPagination.last_page" @click="fetchOrders(ordersPagination.current_page + 1)">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Activity tab -->
              <div v-if="activeTab === 'activity'">
                <div v-if="loadingActivity && !activities.length" class="gl-tab-loading">
                  <div v-for="i in 5" :key="i" class="gl-activity-skel" :style="`animation-delay:${i*50}ms`">
                    <div class="gl-skel-icon-sm"></div>
                    <div style="flex:1;display:flex;flex-direction:column;gap:5px">
                      <div class="gl-skel-line w50"></div>
                      <div class="gl-skel-line w35"></div>
                      <div class="gl-skel-line w20"></div>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <div class="gl-timeline">
                    <div v-for="(item, idx) in activities" :key="idx" class="gl-timeline-item">
                      <div class="gl-timeline-dot" :class="getActivityDotClass(item.type)">
                        <svg v-if="item.type === 'order'" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
                        <svg v-else-if="item.type === 'points_earned'" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                        <svg v-else-if="item.type === 'points_used'" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
                        <svg v-else-if="item.type === 'wallet_topup'" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                        <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      </div>
                      <div class="gl-timeline-line" v-if="idx < activities.length - 1"></div>
                      <div class="gl-timeline-content">
                        <span class="gl-act-title">{{ item.title }}</span>
                        <span class="gl-act-desc">{{ item.description }}</span>
                        <span class="gl-act-time">{{ formatFullDateTime(item.created_at) }}</span>
                      </div>
                    </div>
                    <div v-if="!activities.length" class="gl-empty-act">No activity found.</div>
                  </div>
                  <div v-if="activityPagination.current_page < activityPagination.last_page" class="gl-load-more-wrap">
                    <button class="gl-load-more-btn" @click="loadMoreActivity" :disabled="loadingActivity">
                      {{ loadingActivity ? 'Loading…' : 'Load more activity' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-else class="gl-not-found">Customer not found.</div>
    </div>
  </div>
</template>

<script>
import { ownerCustomerApi } from '@/services/ownerApi';

export default {
  name: 'CustomerDetail',

  data() {
    return {
      loading: true,
      loadingOrders: false,
      loadingActivity: false,
      customer: null,
      orders: [],
      activities: [],
      activeTab: 'orders',
      imgError: false,
      ordersPagination: { current_page: 1, last_page: 1, per_page: 10, total: 0 },
      activityPagination: { current_page: 1, last_page: 1, per_page: 20, total: 0 }
    };
  },

  mounted() {
    this.fetchCustomer();
  },

  methods: {
    formatPrice(price) {
      return parseFloat(price || 0).toFixed(2);
    },

    formatFullDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    formatFullDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('en-MY', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    formatBirthday(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-MY', { month: 'long', day: 'numeric' });
    },

    formatType(type) {
      const map = { delivery: 'Delivery', takeaway: 'Takeaway', dine_in: 'Dine In' };
      return map[type] || type;
    },

    getTierClass(tierName) {
      const t = (tierName || '').toLowerCase();
      if (t === 'platinum') return 'gl-tier-platinum';
      if (t === 'gold') return 'gl-tier-gold';
      if (t === 'silver') return 'gl-tier-silver';
      return 'gl-tier-none';
    },

    getTypeClass(type) {
      const map = { delivery: 'gl-type-delivery', takeaway: 'gl-type-takeaway', dine_in: 'gl-type-dine' };
      return map[type] || 'gl-type-default';
    },

    getStatusClass(status) {
      const map = {
        pending: 'gl-status-pending',
        confirmed: 'gl-status-confirmed',
        preparing: 'gl-status-preparing',
        ready: 'gl-status-ready',
        completed: 'gl-status-completed',
        cancelled: 'gl-status-cancelled'
      };
      return map[status] || 'gl-status-default';
    },

    getActivityDotClass(type) {
      const map = {
        order: 'gl-dot-teal',
        points_earned: 'gl-dot-green',
        points_used: 'gl-dot-amber',
        wallet_topup: 'gl-dot-blue',
        login: 'gl-dot-gray'
      };
      return map[type] || 'gl-dot-gray';
    },

    async fetchCustomer() {
      try {
        this.loading = true;
        const response = await ownerCustomerApi.get(this.$route.params.id);
        this.customer = response.data.data;
        this.fetchOrders();
      } catch (error) {
        console.error('Failed to fetch customer:', error);
        this.customer = null;
      } finally {
        this.loading = false;
      }
    },

    async fetchOrders(page = 1) {
      try {
        this.loadingOrders = true;
        const response = await ownerCustomerApi.getOrders(this.$route.params.id, { page, per_page: 10 });
        this.orders = response.data.data;
        if (response.data.meta) this.ordersPagination = response.data.meta;
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        this.loadingOrders = false;
      }
    },

    async fetchActivity(page = 1, append = false) {
      try {
        this.loadingActivity = true;
        const response = await ownerCustomerApi.getActivity(this.$route.params.id, { page, per_page: 20 });
        this.activities = append ? [...this.activities, ...response.data.data] : response.data.data;
        if (response.data.meta) this.activityPagination = response.data.meta;
      } catch (error) {
        console.error('Failed to fetch activity:', error);
      } finally {
        this.loadingActivity = false;
      }
    },

    switchToActivity() {
      this.activeTab = 'activity';
      if (!this.activities.length) this.fetchActivity();
    },

    loadMoreActivity() {
      this.fetchActivity(this.activityPagination.current_page + 1, true);
    }
  }
};
</script>

<style scoped>
/* ── Page header ── */
.gl-detail-page { padding-bottom: 40px; }
.gl-branch-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 10px; }
.gl-branch-breadcrumb { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #374151; }
.gl-bc-sep { margin: 0 6px; }
.gl-branch-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; letter-spacing: -0.3px; }
.gl-back-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border: 1.5px solid #e8eaf0; border-radius: 10px;
  background: #fff; color: #374151; font-size: 13px; font-weight: 600;
  text-decoration: none; cursor: pointer; transition: all 0.15s;
}
.gl-back-btn:hover { border-color: #900C27; color: #900C27; background: #f0f5f4; }
.gl-back-btn svg { transition: transform 0.15s; }
.gl-back-btn:hover svg { transform: translateX(-2px); }

/* ── Layout grid ── */
.gl-detail-grid { display: grid; grid-template-columns: 280px 1fr; gap: 18px; align-items: start; }
.gl-profile-col {}
.gl-main-col { display: flex; flex-direction: column; gap: 14px; min-width: 0; }

/* ── Profile card ── */
.gl-profile-card {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px;
  overflow: hidden; animation: gl-fadein 0.3s ease;
}
.gl-profile-hero {
  background: linear-gradient(135deg, #1a5248 0%, #900C27 60%, #2d8474 100%);
  padding: 28px 0 48px; display: flex; align-items: center; justify-content: center;
  position: relative;
}
.gl-profile-hero::after {
  content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 40px;
  background: #fff; border-radius: 50% 50% 0 0 / 100% 100% 0 0;
}
.gl-avatar-lg {
  width: 84px; height: 84px; border-radius: 50%; overflow: hidden;
  border: 3px solid rgba(255,255,255,0.9); position: relative; z-index: 1;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
}
.gl-avatar-lg img { width: 100%; height: 100%; object-fit: cover; }
.gl-avatar-placeholder-lg {
  background: #e8f0ee; display: flex; align-items: center; justify-content: center;
  color: #900C27;
}
.gl-profile-body { padding: 4px 20px 20px; text-align: center; }
.gl-profile-name { font-size: 17px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -0.2px; }
.gl-profile-phone { font-size: 13px; color: #6b7280; margin: 0 0 2px; font-weight: 500; }
.gl-profile-email { font-size: 12px; color: #9ca3af; margin: 0 0 10px; }

/* Tier badge */
.gl-tier-badge { display: inline-block; padding: 3px 12px; border-radius: 20px; font-size: 11.5px; font-weight: 600; margin-bottom: 14px; }
.gl-tier-platinum { background: #1e293b; color: #e2e8f0; }
.gl-tier-gold     { background: #fef3c7; color: #92400e; }
.gl-tier-silver   { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.gl-tier-none     { background: #f3f4f6; color: #9ca3af; }

/* Spent box */
.gl-spent-box {
  background: #f0f5f4; border-radius: 12px; padding: 10px 16px;
  margin-bottom: 14px; display: flex; flex-direction: column; align-items: center; gap: 2px;
}
.gl-spent-label { font-size: 11px; color: #6b7280; }
.gl-spent-val { font-size: 20px; font-weight: 800; color: #900C27; letter-spacing: -0.5px; }

/* Profile meta list */
.gl-profile-meta-list { display: flex; flex-direction: column; gap: 0; border-top: 1.5px solid #f5f6f8; padding-top: 12px; margin-top: 2px; text-align: left; }
.gl-meta-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f9fafb; }
.gl-meta-row:last-child { border-bottom: none; }
.gl-meta-lbl { font-size: 12px; color: #9ca3af; font-weight: 500; }
.gl-meta-val { font-size: 12.5px; color: #374151; font-weight: 500; }

/* ── Stats row ── */
.gl-stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.gl-stat-box {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  padding: 16px 18px; display: flex; flex-direction: column; gap: 3px;
  animation: gl-fadein 0.35s ease;
}
.gl-stat-box-teal { background: #f0f5f4; border-color: #d4e4e1; }
.gl-stat-box-blue { background: #eff6ff; border-color: #dbeafe; }
.gl-stat-num { font-size: 20px; font-weight: 800; color: #111827; letter-spacing: -0.5px; line-height: 1.1; }
.gl-stat-box-teal .gl-stat-num { color: #900C27; }
.gl-stat-box-blue .gl-stat-num { color: #2563eb; }
.gl-stat-lbl { font-size: 11.5px; color: #9ca3af; font-weight: 500; }

/* ── Tabs card ── */
.gl-tabs-card {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  overflow: hidden; animation: gl-fadein 0.4s ease;
}
.gl-tab-bar {
  display: flex; gap: 0; border-bottom: 1.5px solid #f0f1f3;
  padding: 0 20px;
}
.gl-tab-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 13px 4px; margin-right: 20px; background: none; border: none;
  font-size: 13.5px; font-weight: 600; color: #9ca3af; cursor: pointer;
  border-bottom: 2px solid transparent; margin-bottom: -1.5px; transition: all 0.15s;
}
.gl-tab-btn:hover { color: #374151; }
.gl-tab-active { color: #900C27 !important; border-bottom-color: #900C27 !important; }
.gl-tab-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 18px; padding: 0 5px;
  background: #f0f5f4; color: #900C27; border-radius: 20px;
  font-size: 11px; font-weight: 700;
}

/* ── Table ── */
.gl-table-wrap { overflow-x: auto; }
.gl-table { width: 100%; min-width: 640px; border-collapse: collapse; font-size: 13.5px; }
.gl-table thead tr { background: #f8f9fa; border-bottom: 1.5px solid #f0f1f3; }
.gl-table thead th { padding: 10px 16px; font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; white-space: nowrap; }
.gl-table thead th.th-right { text-align: right; }
.gl-table tbody tr { border-bottom: 1px solid #f5f6f8; transition: background 0.1s; }
.gl-table tbody tr:last-child { border-bottom: none; }
.gl-table tbody tr:hover { background: #fafafa; }
.gl-table tbody td { padding: 11px 16px; color: #374151; vertical-align: middle; }
.gl-table tbody td.td-right { text-align: right; }

.gl-order-num { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 12px; font-weight: 600; color: #900C27; letter-spacing: 0.02em; }
.gl-branch-name { font-size: 13px; color: #6b7280; }
.gl-order-total { font-size: 13.5px; font-weight: 700; color: #111827; display: block; white-space: nowrap; }
.gl-points-earned { font-size: 11.5px; color: #900C27; font-weight: 600; white-space: nowrap; }
.gl-date-cell { font-size: 12.5px; color: #6b7280; white-space: nowrap; }
.gl-empty-cell { text-align: center; color: #9ca3af; padding: 40px 16px; font-size: 13.5px; }

/* Type badges */
.gl-type-badge { display: inline-block; padding: 3px 9px; border-radius: 6px; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
.gl-type-delivery { background: #eff6ff; color: #2563eb; }
.gl-type-takeaway { background: #f0fdf4; color: #15803d; }
.gl-type-dine    { background: #fdf4ff; color: #9333ea; }
.gl-type-default { background: #f3f4f6; color: #6b7280; }

/* Status badges */
.gl-status-badge { display: inline-block; padding: 3px 9px; border-radius: 6px; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
.gl-status-pending   { background: #fef3c7; color: #d97706; }
.gl-status-confirmed { background: #dbeafe; color: #2563eb; }
.gl-status-preparing { background: #ddf4ff; color: #0369a1; }
.gl-status-ready     { background: #dff0ec; color: #900C27; }
.gl-status-completed { background: #dff0ec; color: #900C27; }
.gl-status-cancelled { background: #fee2e2; color: #dc2626; }
.gl-status-default   { background: #f3f4f6; color: #6b7280; }

/* ── Pagination ── */
.gl-pagination { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 14px; border-top: 1px solid #f5f6f8; }
.gl-page-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border: 1.5px solid #e8eaf0; border-radius: 8px;
  background: #fff; color: #374151; cursor: pointer; transition: all 0.15s;
}
.gl-page-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; background: #f0f5f4; }
.gl-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.gl-page-info { font-size: 13px; color: #6b7280; font-weight: 600; min-width: 60px; text-align: center; }

/* ── Activity timeline ── */
.gl-timeline { padding: 12px 20px 4px; }
.gl-timeline-item { display: flex; gap: 12px; position: relative; padding-bottom: 18px; }
.gl-timeline-item:last-child { padding-bottom: 4px; }
.gl-timeline-dot {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  position: relative; z-index: 1;
}
.gl-dot-teal  { background: #dff0ec; color: #900C27; }
.gl-dot-green { background: #d1fae5; color: #059669; }
.gl-dot-amber { background: #fef3c7; color: #d97706; }
.gl-dot-blue  { background: #dbeafe; color: #2563eb; }
.gl-dot-gray  { background: #f3f4f6; color: #6b7280; }
.gl-timeline-line {
  position: absolute; left: 14px; top: 30px; bottom: 0; width: 1.5px;
  background: #f0f1f3;
}
.gl-timeline-content { display: flex; flex-direction: column; gap: 2px; padding-top: 4px; }
.gl-act-title { font-size: 13.5px; font-weight: 600; color: #111827; }
.gl-act-desc { font-size: 12.5px; color: #6b7280; }
.gl-act-time { font-size: 11.5px; color: #b0b7c3; }
.gl-empty-act { text-align: center; color: #9ca3af; padding: 40px 0; font-size: 13.5px; }

.gl-load-more-wrap { padding: 12px 20px 16px; border-top: 1px solid #f5f6f8; text-align: center; }
.gl-load-more-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 18px; border: 1.5px solid #e8eaf0; border-radius: 9px;
  background: #fff; color: #374151; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
}
.gl-load-more-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; background: #f0f5f4; }
.gl-load-more-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Tab loading */
.gl-tab-loading { padding: 16px; }
.gl-skel-table-inner { display: flex; flex-direction: column; gap: 0; }
.gl-skel-row-inner {
  display: flex; align-items: center; gap: 24px;
  padding: 13px 16px; border-bottom: 1px solid #f5f6f8;
  animation: gl-pulse 1.4s ease-in-out infinite;
}
.gl-activity-skel {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 14px 0; border-bottom: 1px solid #f9fafb;
  animation: gl-pulse 1.4s ease-in-out infinite;
}
.gl-activity-skel:last-child { border-bottom: none; }
.gl-skel-icon-sm { width: 30px; height: 30px; border-radius: 50%; background: #f0f1f3; flex-shrink: 0; }

/* ── Skeleton ── */
.gl-skel-card { background: #f8f9fa; border: 1.5px solid #f0f1f3; border-radius: 16px; animation: gl-pulse 1.4s ease-in-out infinite; }
.gl-skel-avatar-lg { width: 84px; height: 84px; border-radius: 50%; background: #e5e7eb; margin: 0 auto; }
.gl-skel-spent { height: 68px; background: #f0f1f3; border-radius: 12px; margin: 12px 0; }
.gl-skel-badge-sm { width: 64px; height: 22px; background: #f0f1f3; border-radius: 20px; margin: 0 auto 14px; }
.gl-skel-line { height: 11px; background: #f0f1f3; border-radius: 5px; }
.gl-skel-line.mx-auto { margin-left: auto; margin-right: auto; }
.gl-skel-line.mb-2 { margin-bottom: 8px; }
.gl-skel-line.mb-3 { margin-bottom: 12px; }
.gl-skel-line.w15 { width: 15%; }
.gl-skel-line.w20 { width: 20%; }
.gl-skel-line.w25 { width: 25%; }
.gl-skel-line.w30 { width: 30%; }
.gl-skel-line.w35 { width: 35%; }
.gl-skel-line.w40 { width: 40%; }
.gl-skel-line.w45 { width: 45%; }
.gl-skel-line.w50 { width: 50%; }
.gl-skel-line.w60 { width: 60%; }

/* ── Not found ── */
.gl-not-found {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  padding: 48px; text-align: center; color: #9ca3af; font-size: 14px;
}

/* ── Animations ── */
@keyframes gl-fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes gl-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .gl-branch-header { padding: 14px 0 8px; gap: 8px; }
  .gl-branch-title { font-size: 18px; }
  .gl-branch-breadcrumb { display: none; }
  .gl-back-btn { padding: 7px 10px; }
  .gl-back-btn span { display: none; }

  .gl-detail-grid { grid-template-columns: 1fr; gap: 12px; }

  .gl-profile-hero { padding: 20px 0 40px; }
  .gl-avatar-lg { width: 68px; height: 68px; }
  .gl-profile-body { padding: 4px 16px 16px; }
  .gl-profile-name { font-size: 15px; }

  .gl-stats-row { grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .gl-stat-box { padding: 12px 10px; border-radius: 12px; }
  .gl-stat-num { font-size: 15px; }
  .gl-stat-lbl { font-size: 10.5px; }

  .gl-tab-bar { padding: 0 14px; }
  .gl-tab-btn { padding: 11px 4px; margin-right: 14px; font-size: 13px; }

  /* Hide Branch (col 2) only — table scrolls horizontally for the rest */
  .gl-table thead th:nth-child(2),
  .gl-table tbody td:nth-child(2) { display: none; }

  .gl-table thead th,
  .gl-table tbody td { padding: 9px 10px; }

  /* Truncate long order numbers */
  .gl-order-num { max-width: 130px; display: block; overflow: hidden; text-overflow: ellipsis; }

  .gl-timeline { padding: 10px 14px 4px; }

  .gl-tabs-card { max-width: 100%; }
  .gl-table-wrap { overflow-x: auto; }

  .gl-meta-val {
    max-width: 55%;
    text-align: right;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>

<style>
/* Dark mode */
body.dark-only .gl-branch-title { color: #f3f4f6; }
body.dark-only .gl-branch-breadcrumb,
body.dark-only .gl-branch-breadcrumb a { color: #6b7280; }
body.dark-only .gl-back-btn { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-back-btn:hover { border-color: #900C27; color: #a7c4be; background: #1a3330; }

body.dark-only .gl-profile-card,
body.dark-only .gl-stat-box,
body.dark-only .gl-tabs-card { background: #1f2937; border-color: #374151; }
body.dark-only .gl-profile-hero::after { background: #1f2937; }
body.dark-only .gl-profile-name { color: #f9fafb; }
body.dark-only .gl-profile-phone { color: #9ca3af; }
body.dark-only .gl-profile-email { color: #6b7280; }
body.dark-only .gl-spent-box { background: #1a3330; }
body.dark-only .gl-profile-meta-list { border-top-color: #374151; }
body.dark-only .gl-meta-row { border-bottom-color: #1f2937; }
body.dark-only .gl-meta-lbl { color: #6b7280; }
body.dark-only .gl-meta-val { color: #d1d5db; }
body.dark-only .gl-stat-box-teal { background: #1a3330; border-color: #2d5249; }
body.dark-only .gl-stat-box-blue { background: #1e2d4a; border-color: #2d4a7a; }
body.dark-only .gl-stat-lbl { color: #6b7280; }
body.dark-only .gl-stat-num { color: #f9fafb; }
body.dark-only .gl-stat-box-teal .gl-stat-num { color: #a7c4be; }
body.dark-only .gl-stat-box-blue .gl-stat-num { color: #93c5fd; }
body.dark-only .gl-tab-bar { border-bottom-color: #374151; }
body.dark-only .gl-tab-btn { color: #6b7280; }
body.dark-only .gl-tab-btn:hover { color: #d1d5db; }
body.dark-only .gl-tab-active { color: #a7c4be !important; border-bottom-color: #900C27 !important; }
body.dark-only .gl-tab-count { background: #1a3330; color: #a7c4be; }
body.dark-only .gl-table thead tr { background: #111827; }
body.dark-only .gl-table thead th { color: #6b7280; }
body.dark-only .gl-table tbody tr { border-bottom-color: #374151; }
body.dark-only .gl-table tbody tr:hover { background: #111827; }
body.dark-only .gl-table tbody td { color: #d1d5db; }
body.dark-only .gl-branch-name { color: #9ca3af; }
body.dark-only .gl-order-total { color: #f9fafb; }
body.dark-only .gl-date-cell { color: #6b7280; }
body.dark-only .gl-empty-cell { color: #4b5563; }
body.dark-only .gl-timeline-line { background: #374151; }
body.dark-only .gl-act-title { color: #f9fafb; }
body.dark-only .gl-act-desc { color: #9ca3af; }
body.dark-only .gl-act-time { color: #4b5563; }
body.dark-only .gl-pagination { border-top-color: #374151; }
body.dark-only .gl-page-btn { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-page-btn:hover:not(:disabled) { border-color: #900C27; color: #a7c4be; background: #1a3330; }
body.dark-only .gl-page-info { color: #6b7280; }
body.dark-only .gl-load-more-btn { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-load-more-btn:hover:not(:disabled) { border-color: #900C27; color: #a7c4be; background: #1a3330; }
body.dark-only .gl-load-more-wrap { border-top-color: #374151; }
body.dark-only .gl-not-found { background: #1f2937; border-color: #374151; }
body.dark-only .gl-skel-card { background: #111827; border-color: #374151; }
body.dark-only .gl-skel-avatar-lg,
body.dark-only .gl-skel-line,
body.dark-only .gl-skel-badge-sm,
body.dark-only .gl-skel-spent,
body.dark-only .gl-skel-icon-sm { background: #374151; }
body.dark-only .gl-skel-row-inner { border-bottom-color: #374151; }

@media (max-width: 767px) {
  body.dark-only .gl-profile-hero::after { background: #1f2937; }
}
</style>
